import axios from 'axios';
import { useEffect, useState } from 'react';
import { useAuthHeader, useAuthUser } from 'react-auth-kit';
import { CalendarDays, ChevronDown, Coins, FileText, Globe, Loader2, MapPin, Minus, Pencil, PencilLine, Plus, Printer, Search, Ticket, User as UserIcon, XCircle } from 'lucide-react';
import User from '../../Models/User';
import datas from "../../Assets/data.json";
import env from 'react-dotenv';
import { Crypter } from '../../Functions/Security/Crypter';
import { Badge, Button, Empty, FormDrawer, IconButton, Input, Modal, PageHeader, Select, Spin, TableStatusBar, useAlert } from '../ui';

type Onglet = 'EN_COURS' | 'HISTORIQUE';

type BadgeColor = 'blue' | 'green' | 'red' | 'amber' | 'gray' | 'violet';

/** Statuts de ticket encore modifiables (annulation autorisée). */
const STATUTS_TICKET_ACTIFS = ['0', '1', '4', '5'];

/** Formate une date API en libellé lisible (fr). */
function formatDate(value: any): string {
    if (!value) return '—';
    const d = new Date(value);
    if (isNaN(d.getTime())) return String(value);
    return d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
}

function sansAccents(s: string): string {
    return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/** Badge du statut d'un ticket — mapping depuis LibelleStatut puis StatutTicket. */
function StatutTicketBadge({ ticket }: { ticket: any }) {
    let lib = String(ticket?.LibelleStatut ?? '').trim();
    const norm = sansAccents(lib.toUpperCase());

    let color: BadgeColor = 'gray';
    if (norm.includes('APPEL')) color = 'blue';
    else if (norm.includes('CONFIRM') || norm.includes('ATTENT')) color = 'amber';
    else if (norm.includes('VALID')) color = 'green';
    else if (norm.includes('REJET')) color = 'red';
    else if (norm.includes('ABSENT') || norm.includes('ANNUL')) color = 'gray';
    else if (norm.includes('TRANSF')) color = 'violet';
    else {
        // Fallback sur le code statut
        switch (String(ticket?.StatutTicket ?? '')) {
            case '0': color = 'amber'; lib = lib || 'En attente'; break;
            case '1': color = 'amber'; lib = lib || 'Arrivée confirmée'; break;
            case '2': color = 'green'; lib = lib || 'Validé'; break;
            case '3': color = 'red'; lib = lib || 'Rejeté'; break;
            case '4': color = 'blue'; lib = lib || 'Appelé'; break;
            case '5': color = 'violet'; lib = lib || 'Transféré'; break;
            case '6': color = 'gray'; lib = lib || 'Absent'; break;
            case '7': color = 'gray'; lib = lib || 'Annulé'; break;
            default: color = 'gray';
        }
    }
    return <Badge color={color}>{lib || '—'}</Badge>;
}

/** Badge du statut d'une demande — tolérant (libellé ou code). */
function StatutDemandeBadge({ statut }: { statut: any }) {
    const raw = String(statut ?? '').trim();
    const norm = sansAccents(raw.toUpperCase());

    let color: BadgeColor = 'gray';
    let label = raw || '—';

    if (norm.includes('ANNUL') || norm.includes('ABSENT')) color = 'gray';
    else if (norm.includes('REJET')) color = 'red';
    else if (norm.includes('VALID') || norm.includes('SOLD') || norm.includes('TERMINE') || norm.includes('TRAIT')) color = 'green';
    else if (norm.includes('COURS')) color = 'blue';
    else if (norm.includes('ATTENT')) color = 'amber';
    else {
        switch (raw) {
            case '0': color = 'amber'; label = 'En attente'; break;
            case '1': color = 'blue'; label = 'En cours'; break;
            case '2': color = 'green'; label = 'Soldée'; break;
            case '3': color = 'red'; label = 'Rejetée'; break;
            case '6': color = 'gray'; label = 'Absente'; break;
            case '7': color = 'gray'; label = 'Annulée'; break;
            default: color = 'gray';
        }
    }
    return <Badge color={color}>{label}</Badge>;
}

/** Normalise Result : tableau direct ou { Demandes, Tickets, Formulaires }. */
function normalizeResult(result: any): { demandes: any[]; tickets: any[]; formulaires: any[] } {
    if (Array.isArray(result)) {
        const demandes: any[] = [];
        const tickets: any[] = [];
        const formulaires: any[] = [];
        result.forEach((r: any) => {
            if (r?.NumeroTicket != null || r?.TicketId != null || r?.CodeT != null) tickets.push(r);
            else if (r?.CodeForm != null || r?.ReponseId != null) formulaires.push(r);
            else demandes.push(r);
        });
        return { demandes, tickets, formulaires };
    }
    return {
        demandes: Array.isArray(result?.Demandes) ? result.Demandes : [],
        tickets: Array.isArray(result?.Tickets) ? result.Tickets : [],
        formulaires: Array.isArray(result?.Formulaires) ? result.Formulaires : [],
    };
}

/** Ouvre un PDF reçu en Base64 dans un nouvel onglet. */
function openPdfBase64(base64: string) {
    const bin = atob(base64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
    window.open(url);
}

/** Extrait {FileName, Base64} d'une réponse PDF ({Value:{Result}} ou déjà Result). */
function pdfResult(res: any): { FileName?: string; Base64?: string } {
    return res?.Value?.Result ?? res?.Result ?? res ?? {};
}

function formatMontant(n: any): string {
    if (n == null || isNaN(Number(n))) return '—';
    return Number(n).toLocaleString('fr-FR');
}

/* ------------------------- Formulaires (édition) ------------------------- */

type TypeChamp = 'TEXT' | 'NUMBER' | 'DATE' | 'SELECT' | 'CHECKBOX' | 'TEXTAREA'
const TYPES_CONNUS: TypeChamp[] = ['TEXT', 'NUMBER', 'DATE', 'SELECT', 'CHECKBOX', 'TEXTAREA']

interface ChampForm {
    CodeChamp: string
    Libelle: string
    TypeChamp: TypeChamp
    Obligatoire: boolean
    Placeholder?: string
    OptionsJson?: string
}

/** Extrait la liste des champs depuis un Result GET (tableau, {Champs} ou {Formulaire, Champs}). */
function extractChampsList(result: any): any[] {
    if (!result) return []
    if (Array.isArray(result)) {
        if (result.some((x: any) => x && (x.CodeChamp !== undefined || x.TypeChamp !== undefined)))
            return result.filter((x: any) => x && (x.CodeChamp !== undefined || x.TypeChamp !== undefined))
        return result.find((x: any) => Array.isArray(x?.Champs))?.Champs || []
    }
    return Array.isArray(result?.Champs) ? result.Champs : []
}

function normalizeChamps(result: any): ChampForm[] {
    return extractChampsList(result)
        .map((c: any) => {
            const t = String(c?.TypeChamp ?? 'TEXT').toUpperCase()
            return {
                Ordre: c?.Ordre ?? 0,
                CodeChamp: c?.CodeChamp ?? '',
                Libelle: c?.Libelle ?? '',
                TypeChamp: (TYPES_CONNUS.includes(t as TypeChamp) ? t : 'TEXT') as TypeChamp,
                Obligatoire: !!c?.Obligatoire,
                Placeholder: c?.Placeholder ?? '',
                OptionsJson: c?.OptionsJson ?? '',
            }
        })
        .sort((a: any, b: any) => (a.Ordre || 0) - (b.Ordre || 0))
        .map(({ Ordre, ...rest }: any) => rest as ChampForm)
}

function parseOptions(json: any): string[] {
    if (Array.isArray(json)) return json.map(String).filter(Boolean)
    if (!json) return []
    try { const v = JSON.parse(json); return Array.isArray(v) ? v.map(String).filter(Boolean) : [] } catch { return [] }
}

function parseJsonLoose(raw: any): any {
    if (raw == null) return null
    if (typeof raw === 'object') return raw
    try { return JSON.parse(raw) } catch { return null }
}

export default function GestionDemandes() {

    // USER TOKEN
    const authHeader = useAuthHeader()

    // USER SESSION
    const auth = useAuthUser()
    const user: User = JSON.parse(JSON.stringify(auth() || {}))
    const alert = useAlert()

    const [tab, setTab] = useState<Onglet>('EN_COURS')
    const [statutFiltre, setStatutFiltre] = useState('')
    const [canalFiltre, setCanalFiltre] = useState('')
    const [serviceFiltre, setServiceFiltre] = useState('')
    const [operationFiltre, setOperationFiltre] = useState('')
    const [demandes, setDemandes] = useState<any[]>([])
    const [tickets, setTickets] = useState<any[]>([])
    const [formulaires, setFormulaires] = useState<any[]>([])
    const [loading, setLoading] = useState(false)
    const [printLoading, setPrintLoading] = useState<string | null>(null)

    // Filtres serveur (CipherFiltre)
    const [recherche, setRecherche] = useState('')
    const [dateDebut, setDateDebut] = useState('')
    const [dateFin, setDateFin] = useState('')

    // Modale d'annulation d'un ticket
    const [confirm, setConfirm] = useState<{ open: boolean; ticket: any | null; demande: any | null }>({ open: false, ticket: null, demande: null })
    const [reason, setReason] = useState('')
    const [actionLoading, setActionLoading] = useState(false)

    // Édition d'un formulaire rempli (droit B05 — Formulaires/Submit)
    const [editForm, setEditForm] = useState<{
        open: boolean; loading: boolean; saving: boolean
        form: any; demande: any
        champs: ChampForm[]; valeurs: Record<string, any>
        motif: string; erreurs: Record<string, string>
    }>({ open: false, loading: false, saving: false, form: null, demande: null, champs: [], valeurs: {}, motif: '', erreurs: {} })

    // Ajustement billetage / ligne d'opération (Billetage/SaisirBilletage + AjusterOperation)
    const [ajust, setAjust] = useState<{ open: boolean; loading: boolean; demande: any; lignes: any[] }>({ open: false, loading: false, demande: null, lignes: [] })
    const [originesRef, setOriginesRef] = useState<any[]>([])
    const [denomsRef, setDenomsRef] = useState<any[]>([])
    // Ligne dépliée dans le drawer (édition inline — pas de second drawer)
    const [editLigne, setEditLigne] = useState<{
        key: any
        compte: string; montant: string; origine: string; motifOp: string; motif: string
        qtes: Record<string, number>
        savingOp: boolean; savingBil: boolean
    } | null>(null)

    useEffect(() => {
        LoadData('EN_COURS')
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    /** Charge les demandes de l'agence via GET DemandesEnLigne + CipherFiltre (jeton agent). */
    function LoadData(onglet: Onglet = tab, silent = false, over?: { recherche?: string; dateDebut?: string; dateFin?: string }) {
        if (!silent) setLoading(true)
        const r = over?.recherche ?? recherche
        const dd = over?.dateDebut ?? dateDebut
        const df = over?.dateFin ?? dateFin
        const filtre: any = { Etat: onglet === 'EN_COURS' ? 'EN_COURS' : 'TRAITEES' }
        if (r.trim()) filtre.Recherche = r.trim()
        if (dd) filtre.DateDebut = dd
        if (df) filtre.DateFin = df

        const url = env.API_BASE_URL + datas.API_LINKs.Demandes.DemandesEnLigne
            + '?CipherFiltre=' + Crypter.encrypt(JSON.stringify(filtre))

        let config = {
            method: 'get',
            maxBodyLength: Infinity,
            url,
            headers: {
                'Accept': 'text/plain',
                'Authorization': authHeader(),
            }
        };

        axios.request(config)
            .then((response: any) => {
                setLoading(false)
                const res = JSON.parse(Crypter.decrypt(response.data)).Value
                if (res.StatutCode == 200) {
                    const { demandes, tickets, formulaires } = normalizeResult(res.Result)
                    setDemandes(demandes)
                    setTickets(tickets)
                    setFormulaires(formulaires)
                }
                else {
                    setDemandes([])
                    setTickets([])
                    setFormulaires([])
                    alert.warning(res.Message)
                }
            })
            .catch((error: any) => {
                setLoading(false)
                alert.error('Error', error)
                console.log(error);
            });
    }

    // Rafraîchissement automatique toutes les 30 s de l'onglet « En cours »
    useEffect(() => {
        if (tab !== 'EN_COURS') return
        const timer = setInterval(() => LoadData('EN_COURS', true), 30000)
        return () => clearInterval(timer)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, recherche, dateDebut, dateFin])

    /** Change d'onglet : purge les listes puis recharge — jamais de contenu mélangé. */
    function switchTab(onglet: Onglet) {
        if (onglet === tab) return
        setTab(onglet)
        setStatutFiltre('')
        setCanalFiltre('')
        setServiceFiltre('')
        setOperationFiltre('')
        setDemandes([])
        setTickets([])
        setFormulaires([])
        LoadData(onglet)
    }

    /** Raccourcis de période de l'onglet Historique (appliqués immédiatement). */
    function applyPreset(key: 'today' | '7j' | '30j' | 'month') {
        const end = new Date()
        const start = new Date()
        if (key === '7j') start.setDate(start.getDate() - 6)
        else if (key === '30j') start.setDate(start.getDate() - 29)
        else if (key === 'month') start.setDate(1)
        const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
        const dd = iso(start), df = iso(end)
        setDateDebut(dd)
        setDateFin(df)
        LoadData('HISTORIQUE', false, { dateDebut: dd, dateFin: df })
    }

    /** GET Formulaires/Imprimer — PDF d'un formulaire rempli (CodeForm + CodeDemande). */
    function imprimerFormulaire(f: any, demande: any) {
        const key = f.ReponseId ?? f.CodeForm
        setPrintLoading(`form-${key}`)
        const payload = {
            CodeForm: f.CodeForm,
            CodeAbonne: user.CodeAbonne,
            CodeFiliale: demande?.CodeFiliale || f.CodeFiliale || user.CodeFiliale,
            CodeDemande: f.CodeDemande || demande?.CodeDemande,
            Orientation: 'PORTRAIT',
        }
        axios.request({
            method: 'post',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.Formulaires.Imprimer,
            headers: { 'Content-Type': 'application/json', 'Authorization': authHeader() },
            data: Crypter.encrypt(JSON.stringify(payload))
        })
            .then((response: any) => {
                const res = JSON.parse(Crypter.decrypt(response.data)).Value
                if (res.StatutCode == 200 && pdfResult(res)?.Base64) openPdfBase64(pdfResult(res).Base64!)
                else alert.warning(res.Message || 'Impression indisponible')
            })
            .catch((e: any) => { alert.error('Erreur', e); console.log(e) })
            .finally(() => setPrintLoading(null))
    }

    /** GET Billetage/Bordereau — PDF du bordereau de billetage de la demande. */
    function imprimerBordereau(demande: any) {
        const code = demande?.CodeDemande
        if (!code) return
        setPrintLoading(`bl-${code}`)
        axios.request({
            method: 'get',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.Billetage.Bordereau + Crypter.encrypt(code),
            headers: { 'Accept': 'text/plain', 'Authorization': authHeader() }
        })
            .then((response: any) => {
                const res = JSON.parse(Crypter.decrypt(response.data)).Value
                if (res.StatutCode == 200 && pdfResult(res)?.Base64) openPdfBase64(pdfResult(res).Base64!)
                else alert.warning(res.Message || 'Bordereau indisponible')
            })
            .catch((e: any) => { alert.error('Erreur', e); console.log(e) })
            .finally(() => setPrintLoading(null))
    }

    /* ------------------------- Ajustements (B05) ------------------------- */

    /** Appel POST générique : corps chiffré, réponse déchiffrée → Value. */
    function postApi(url: string, payload: any): Promise<any> {
        return axios.request({
            method: 'post',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + url,
            headers: { 'Content-Type': 'application/json', 'Authorization': authHeader() },
            data: Crypter.encrypt(JSON.stringify(payload))
        }).then((response: any) => JSON.parse(Crypter.decrypt(response.data)).Value)
    }

    /** GET ManageCatalogue — référentiels BO (ORIGINE, DENOMINATION). */
    function getCatalogueRef(entite: 'ORIGINE' | 'DENOMINATION'): Promise<any> {
        return axios.request({
            method: 'get',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.Operations.ManageCatalogueGet
                + Crypter.encrypt(JSON.stringify({ Entite: entite, Option: 'LIST', CodeAbonne: user.CodeAbonne })),
            headers: { 'Accept': 'text/plain', 'Authorization': authHeader() },
        }).then((response: any) => {
            const res = JSON.parse(Crypter.decrypt(response.data)).Value
            const r = res?.Result
            return Array.isArray(r) ? r : (Array.isArray(r?.Table) ? r.Table : [])
        }).catch(() => [])
    }

    function loadReferentiels() {
        getCatalogueRef('ORIGINE').then(list => setOriginesRef(list))
        getCatalogueRef('DENOMINATION').then(list => setDenomsRef(list))
    }

    /* ----- Édition d'un formulaire rempli ----- */

    /** Ouvre la modale : champs du form (ManageFormulaire GET) + valeurs actuelles (Formulaires/Reponses). */
    function openEditForm(f: any, demande: any) {
        setEditForm({ open: true, loading: true, saving: false, form: f, demande, champs: [], valeurs: {}, motif: '', erreurs: {} })
        Promise.all([
            postApi(datas.API_LINKs.Formulaires.ManageFormulaire, { Option: 'GET', CodeForm: f.CodeForm, CodeAbonne: user.CodeAbonne }).catch(() => null),
            postApi(datas.API_LINKs.Formulaires.Reponses, { CodeDemande: f.CodeDemande || demande?.CodeDemande }).catch(() => null),
        ]).then(([resForm, resRep]) => {
            let champs = resForm?.StatutCode == 200 ? normalizeChamps(resForm.Result) : []
            let valeurs: Record<string, any> = {}
            if (resRep?.StatutCode == 200) {
                const list = Array.isArray(resRep.Result) ? resRep.Result : (resRep.Result?.Reponses || [])
                const r = list.find((x: any) =>
                    (f.ReponseId != null && (x.ReponseId === f.ReponseId || x.Id === f.ReponseId)) ||
                    (x.CodeForm === f.CodeForm && (f.CodeOperationLigne == null || x.CodeOperationLigne === f.CodeOperationLigne))
                ) || list.find((x: any) => x.CodeForm === f.CodeForm)
                valeurs = parseJsonLoose(r?.ReponseJson ?? r?.Reponses ?? r?.Valeurs) || {}
            }
            // Champs présents dans les valeurs mais absents de la définition : champs génériques
            const known = new Set(champs.map(c => c.CodeChamp))
            Object.keys(valeurs).filter(k => !known.has(k)).forEach(k =>
                champs.push({ CodeChamp: k, Libelle: k, TypeChamp: 'TEXT', Obligatoire: false }))
            setEditForm(prev => ({ ...prev, loading: false, champs, valeurs }))
        })
    }

    /** POST Formulaires/Submit — enregistre la correction (ReponseJson complet + MotifAjustement). */
    function submitEditForm() {
        const { form, demande, champs, valeurs, motif } = editForm
        if (!form) return
        if (!motif.trim()) { alert.warning('Le motif d\'ajustement est requis'); return }
        const erreurs: Record<string, string> = {}
        champs.forEach(c => {
            const v = valeurs[c.CodeChamp]
            if (c.Obligatoire && (v == null || String(v).trim() === '' || v === false)) erreurs[c.CodeChamp] = 'Champ obligatoire'
        })
        if (Object.keys(erreurs).length) { setEditForm(prev => ({ ...prev, erreurs })); return }

        setEditForm(prev => ({ ...prev, saving: true, erreurs: {} }))
        postApi(datas.API_LINKs.Formulaires.Submit, {
            CodeForm: form.CodeForm,
            CodeDemande: form.CodeDemande || demande?.CodeDemande,
            CodeOperationLigne: form.CodeOperationLigne,
            ReponseJson: JSON.stringify(valeurs),
            MotifAjustement: motif.trim(),
        }).then((res: any) => {
            if (res?.StatutCode == 200) {
                alert.success(res.Message || 'Formulaire ajusté')
                setEditForm(prev => ({ ...prev, open: false, saving: false }))
                LoadData(tab)
            } else {
                setEditForm(prev => ({ ...prev, saving: false, erreurs: erreursChamps(res?.Result) }))
                alert.warning(res?.Message || 'Ajustement refusé')
            }
        }).catch((error: any) => {
            // 400 → erreurs par champ chiffrées dans error.response.data
            const res = decryptValue(error?.response?.data)
            setEditForm(prev => ({ ...prev, saving: false, erreurs: erreursChamps(res?.Result) }))
            alert.warning(res?.Message || error?.message || 'Ajustement refusé')
        })
    }

    /** Déchiffre un corps d'erreur API → Value (tolérant). */
    function decryptValue(data: any): any {
        if (!data) return null
        try {
            const dec = typeof data === 'string' ? Crypter.decrypt(data) : JSON.stringify(data)
            const parsed = parseJsonLoose(dec)
            return parsed?.Value ?? parsed
        } catch { return null }
    }

    /** Extrait {CodeChamp: Erreur} d'un Result d'erreur [{CodeChamp, Libelle, Erreur}]. */
    function erreursChamps(result: any): Record<string, string> {
        const map: Record<string, string> = {}
        const list = Array.isArray(result) ? result : []
        list.forEach((e: any) => { if (e?.CodeChamp) map[e.CodeChamp] = e.Erreur || 'Valeur invalide' })
        return map
    }

    /* ----- Ajustement billetage / ligne d'opération ----- */

    /** Charge les lignes d'opération de la demande via TicketDetails de chaque ticket. */
    function openAjust(demande: any) {
        setAjust({ open: true, loading: true, demande, lignes: [] })
        setEditLigne(null)
        loadReferentiels()
        const ticketsDem = ticketsParDemande[demande?.CodeDemande] || []
        Promise.all(ticketsDem.map((t: any) =>
            axios.request({
                method: 'get', maxBodyLength: Infinity,
                url: env.API_BASE_URL + datas.API_LINKs.Tickets.TicketDetails + Crypter.encrypt(t.CodeT),
                headers: { 'Accept': 'text/plain', 'Authorization': authHeader() },
            }).then((response: any) => {
                const res = JSON.parse(Crypter.decrypt(response.data)).Value
                let r = res?.Result
                if (typeof r === 'string') r = parseJsonLoose(r)
                const lignes = r?.Lignes || r?.LignesOperations || r?.Operations || r?.OperationLignes || []
                return Array.isArray(lignes) ? lignes : []
            }).catch(() => [])
        )).then((nested: any[][]) => {
            const seen = new Set<any>()
            const lignes: any[] = []
            nested.flat().forEach((l: any) => {
                const key = l?.CodeOperationLigne ?? l?.Id ?? l
                if (key == null || seen.has(key)) return
                seen.add(key)
                lignes.push(l)
            })
            // AvecBilletage au niveau demande mais aucun flag de ligne : proposer sur toutes
            if (demande?.AvecBilletage && lignes.length && !lignes.some(l => l?.NecessiteBilletage || l?.AvecBilletage || l?.Billetage || l?.LignesBilletage))
                lignes.forEach(l => l.AvecBilletage = true)
            setAjust({ open: true, loading: false, demande, lignes })
        })
    }

    /** Déplie / replie la carte d'une ligne et pré-remplit le formulaire inline. */
    function toggleLigne(ligne: any, i: number) {
        const key = ligne?.CodeOperationLigne ?? i
        if (editLigne?.key === key) { setEditLigne(null); return }
        const qtes: Record<string, number> = {}
        const lignes = ligne?.Billetage?.Lignes || ligne?.LignesBilletage || ligne?.Billetage || ligne?.Coupures || []
        ;(Array.isArray(lignes) ? lignes : []).forEach((c: any) => {
            qtes[`${c.TypeCoupure}-${c.Valeur}`] = Number(c.Nombre ?? c.Quantite ?? 0)
        })
        setEditLigne({
            key,
            compte: ligne?.NumeroCompteSaisi ?? '',
            montant: String(ligne?.MontantSaisi ?? ligne?.MontantDeclare ?? ''),
            origine: ligne?.CodeOrigine ?? '',
            motifOp: ligne?.Motif ?? '',
            motif: '',
            qtes,
            savingOp: false, savingBil: false,
        })
    }

    /** POST Billetage/SaisirBilletage — recompte les coupures d'une ligne. */
    function submitBilEdit(ligne: any) {
        if (!editLigne) return
        if (!editLigne.motif.trim()) { alert.warning('Le motif d\'ajustement est requis'); return }
        const lignes = denomsRef
            .filter((d: any) => (editLigne.qtes[`${d.TypeCoupure}-${d.Valeur}`] || 0) > 0)
            .map((d: any) => ({ TypeCoupure: d.TypeCoupure, Valeur: Number(d.Valeur), Nombre: editLigne.qtes[`${d.TypeCoupure}-${d.Valeur}`], CodeDevise: d.CodeDevise }))
        setEditLigne({ ...editLigne, savingBil: true })
        postApi(datas.API_LINKs.Billetage.SaisirBilletage, {
            CodeOperationLigne: ligne?.CodeOperationLigne,
            MontantSaisi: editLigne.montant !== '' ? Number(editLigne.montant) : undefined,
            Lignes: lignes,
            MotifAjustement: editLigne.motif.trim(),
        }).then((res: any) => {
            if (res?.StatutCode == 200) {
                alert.success(res.Message || 'Billetage enregistré')
                setEditLigne(null)
                LoadData(tab)
            } else {
                setEditLigne(prev => prev && { ...prev, savingBil: false })
                alert.warning(res?.Message || 'Saisie refusée')
            }
        }).catch((error: any) => {
            const res = decryptValue(error?.response?.data)
            setEditLigne(prev => prev && { ...prev, savingBil: false })
            alert.warning(res?.Message || error?.message || 'Saisie refusée')
        })
    }

    /** POST Billetage/AjusterOperation — corrige compte / origine / motif / montant d'une ligne. */
    function submitOpEdit(ligne: any) {
        if (!editLigne) return
        if (!editLigne.motif.trim()) { alert.warning('Le motif d\'ajustement est requis'); return }
        setEditLigne({ ...editLigne, savingOp: true })
        const payload: any = { CodeOperationLigne: ligne?.CodeOperationLigne, MotifAjustement: editLigne.motif.trim() }
        if (editLigne.compte.trim()) payload.NumeroCompteSaisi = editLigne.compte.trim()
        if (editLigne.montant !== '') payload.MontantSaisi = Number(editLigne.montant)
        if (editLigne.origine) payload.CodeOrigine = editLigne.origine
        if (editLigne.motifOp.trim()) payload.Motif = editLigne.motifOp.trim()
        postApi(datas.API_LINKs.Billetage.AjusterOperation, payload)
            .then((res: any) => {
                if (res?.StatutCode == 200) {
                    alert.success(res.Message || 'Opération ajustée')
                    setEditLigne(null)
                    LoadData(tab)
                } else {
                    setEditLigne(prev => prev && { ...prev, savingOp: false })
                    alert.warning(res?.Message || 'Ajustement refusé')
                }
            }).catch((error: any) => {
                const res = decryptValue(error?.response?.data)
                setEditLigne(prev => prev && { ...prev, savingOp: false })
                alert.warning(res?.Message || error?.message || 'Ajustement refusé')
            })
    }

    /** POST ManageTicket {Option:'Cancel'} — annule un ticket actif. */
    function AnnulerTicket() {
        const t = confirm.ticket
        if (!t) return
        setActionLoading(true)
        const payload = {
            Option: 'Cancel',
            CodeT: t.CodeT,
            CodeS: t.CodeS,
            CodeFiliale: confirm.demande?.CodeFiliale || t.CodeFiliale || user.CodeFiliale,
            CodeU: user.CodeU,
            Reason: reason.trim(),
        }

        let config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.Demandes.ManageTicket,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': authHeader(),
            },
            data: Crypter.encrypt(JSON.stringify(payload))
        };

        axios.request(config)
            .then((response: any) => {
                setActionLoading(false)
                const res = JSON.parse(Crypter.decrypt(response.data)).Value
                if (res.StatutCode == 200) {
                    alert.success(res.Message || 'Ticket annulé')
                    setConfirm({ open: false, ticket: null, demande: null })
                    setReason('')
                    LoadData()
                }
                else {
                    alert.warning(res.Message)
                }
            })
            .catch((error: any) => {
                setActionLoading(false)
                alert.error('Error', error)
                console.log(error);
            });
    }

    // Statuts, services et opérations distincts présents dans les tickets chargés (options des filtres)
    const statutsDispo = Array.from(
        new Set(tickets.map((t: any) => String(t?.LibelleStatut || '').trim()).filter(Boolean))
    ).sort()
    const servicesDispo = Array.from(
        new Set(tickets.map((t: any) => String(t?.LibelleService || t?.CodeS || '').trim()).filter(Boolean))
    ).sort()
    const operationsDispo = Array.from(
        new Set(tickets.map((t: any) => String(t?.LibelleOperation || t?.Operation || t?.CodeOperation || '').trim()).filter(Boolean))
    ).sort()

    // Filtres client : statut, service, opération (sur les tickets)
    const ticketsAff = tickets.filter((t: any) => {
        if (statutFiltre && String(t?.LibelleStatut || '').trim() !== statutFiltre) return false
        if (serviceFiltre && String(t?.LibelleService || t?.CodeS || '').trim() !== serviceFiltre) return false
        if (operationFiltre && String(t?.LibelleOperation || t?.Operation || t?.CodeOperation || '').trim() !== operationFiltre) return false
        return true
    })

    // Regroupement des tickets et formulaires par demande
    const ticketsParDemande: Record<string, any[]> = {}
    ticketsAff.forEach((t: any) => {
        const key = t.CodeDemande || '—'
        if (!ticketsParDemande[key]) ticketsParDemande[key] = []
        ticketsParDemande[key].push(t)
    })
    const formulairesParDemande: Record<string, any[]> = {}
    formulaires.forEach((f: any) => {
        const key = f.CodeDemande || '—'
        if (!formulairesParDemande[key]) formulairesParDemande[key] = []
        formulairesParDemande[key].push(f)
    })
    // Tickets rattachés à aucune demande connue de la liste
    const ticketsOrphelins = ticketsAff.filter((t: any) => !demandes.some((d: any) => d.CodeDemande === t.CodeDemande))

    // Canaux (sources) distincts présents dans les demandes chargées
    const canauxDispo = Array.from(
        new Set(demandes.map((d: any) => String(d?.Canal || '').trim()).filter(Boolean))
    ).sort()

    // Demandes affichées : filtre source + filtres tickets (statut / service / opération)
    const demandesAff = demandes.filter((d: any) => {
        if (canalFiltre && String(d?.Canal || '').trim() !== canalFiltre) return false
        if ((statutFiltre || serviceFiltre || operationFiltre) && (ticketsParDemande[d.CodeDemande] || []).length === 0) return false
        return true
    })

    const isEmpty = demandesAff.length === 0 && ticketsOrphelins.length === 0

    /** Sous-liste des tickets d'une demande. */
    function renderTickets(list: any[], demande: any) {
        if (list.length === 0) {
            return <p className="mt-3 text-xs text-[var(--eb-text-muted)]">Aucun ticket associé</p>
        }
        return (
            <div className="mt-3 border-t border-[var(--eb-border)] pt-3 space-y-2">
                {list.map((t: any, i: number) => {
                    const actif = STATUTS_TICKET_ACTIFS.includes(String(t.StatutTicket))
                    return (
                        <div key={t.TicketId ?? t.CodeT ?? i} className="flex items-center justify-between gap-3 flex-wrap">
                            <div className="flex items-center gap-3 flex-wrap">
                                <span className="inline-flex items-center gap-1.5 font-mono text-sm font-bold text-[var(--eb-text)]">
                                    <Ticket size={14} className="text-[var(--eb-text-muted)]" />
                                    {t.NumeroTicket || t.CodeT || '—'}
                                </span>
                                <span className="text-sm text-[var(--eb-text-muted)]">{t.LibelleService || t.CodeS || '—'}</span>
                                <StatutTicketBadge ticket={t} />
                                {t.NumBox != null && t.NumBox !== '' && (
                                    <span className="text-xs text-[var(--eb-text-muted)]">Box {t.NumBox}</span>
                                )}
                            </div>
                            {actif && (
                                <IconButton
                                    tooltip="Annuler le ticket"
                                    color="danger"
                                    size={30}
                                    onClick={() => { setConfirm({ open: true, ticket: t, demande }); setReason('') }}
                                >
                                    <XCircle size={15} />
                                </IconButton>
                            )}
                        </div>
                    )
                })}
            </div>
        )
    }

    /** Sous-liste des formulaires remplis d'une demande (impression PDF). */
    function renderFormulaires(list: any[], demande: any) {
        if (list.length === 0) return null
        return (
            <div className="mt-3 border-t border-[var(--eb-border)] pt-3 space-y-2">
                {list.map((f: any, i: number) => {
                    const ajuste = f.ModifieParCodeU != null || f.MotifAjustement != null
                    const key = f.ReponseId ?? `${f.CodeForm}-${i}`
                    return (
                        <div key={key} className="flex items-center justify-between gap-3 flex-wrap">
                            <div className="flex items-center gap-3 flex-wrap">
                                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--eb-text)]">
                                    <FileText size={14} className="text-[var(--eb-text-muted)]" />
                                    {f.LibelleForm || f.CodeForm || 'Formulaire'}
                                </span>
                                <Badge color={f.SaisiPar === 'CLIENT' ? 'blue' : 'violet'}>
                                    {f.SaisiPar === 'CLIENT' ? 'Client' : 'Opérateur'}
                                </Badge>
                                {ajuste && <Badge color="amber">Ajusté{f.MotifAjustement ? ` — ${f.MotifAjustement}` : ''}</Badge>}
                                {f.Verrouille && <Badge color="gray">Verrouillé</Badge>}
                                {f.DateSaisie && <span className="text-xs text-[var(--eb-text-muted)]">{formatDate(f.DateSaisie)}</span>}
                            </div>
                            <div className="flex items-center gap-1">
                                {!f.Verrouille && (
                                    <IconButton
                                        tooltip="Modifier le formulaire"
                                        size={30}
                                        onClick={() => openEditForm(f, demande)}
                                    >
                                        <Pencil size={15} />
                                    </IconButton>
                                )}
                                <IconButton
                                    tooltip="Imprimer le formulaire (PDF)"
                                    size={30}
                                    disabled={printLoading === `form-${key}`}
                                    onClick={() => imprimerFormulaire(f, demande)}
                                >
                                    {printLoading === `form-${key}` ? <Loader2 size={15} className="animate-spin" /> : <Printer size={15} />}
                                </IconButton>
                            </div>
                        </div>
                    )
                })}
            </div>
        )
    }

    return (
        <Spin spinning={loading} >
            <PageHeader
                title="Demandes en ligne"
                subtitle="Suivi des demandes clients et de leurs tickets"
            />

            {/* Onglets segmented */}
            <div className="inline-flex p-1 rounded-xl bg-[var(--eb-bg)] border border-[var(--eb-border)] mb-4">
                {([{ key: 'EN_COURS', label: 'En cours' }, { key: 'HISTORIQUE', label: 'Historique' }] as { key: Onglet; label: string }[]).map(o => (
                    <button
                        key={o.key}
                        onClick={() => switchTab(o.key)}
                        className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-colors ${tab === o.key
                            ? 'bg-[var(--eb-surface)] text-[var(--eb-text)] shadow-sm'
                            : 'text-[var(--eb-text-muted)] hover:text-[var(--eb-text)]'}`}
                    >
                        {o.label}
                    </button>
                ))}
            </div>

            {/* Filtres — propres à l'onglet actif (CipherFiltre) */}
            <div className="eb-card p-3 mb-4">
                <div className="flex items-end gap-2 flex-wrap">
                    <div className="w-72 max-w-full">
                        <Input
                            label="Recherche"
                            startIcon={<Search size={14} />}
                            placeholder="Nom, téléphone, code client, demande ou ticket…"
                            value={recherche}
                            onChange={(e: any) => setRecherche(e.target.value)}
                            onKeyDown={(e: any) => { if (e.key === 'Enter') LoadData(tab) }}
                        />
                    </div>
                    <div className="w-44">
                        <Select
                            label="Statut du ticket"
                            value={statutFiltre}
                            onChange={setStatutFiltre}
                            options={[
                                { value: '', label: 'Tous les statuts' },
                                ...statutsDispo.map(s => ({ value: s, label: s })),
                            ]}
                        />
                    </div>
                    <div className="w-44">
                        <Select
                            label="Service"
                            value={serviceFiltre}
                            onChange={setServiceFiltre}
                            options={[
                                { value: '', label: 'Tous' },
                                ...servicesDispo.map(s => ({ value: s, label: s })),
                            ]}
                        />
                    </div>
                    {operationsDispo.length > 0 && (
                        <div className="w-44">
                            <Select
                                label="Opération"
                                value={operationFiltre}
                                onChange={setOperationFiltre}
                                options={[
                                    { value: '', label: 'Toutes' },
                                    ...operationsDispo.map(o => ({ value: o, label: o })),
                                ]}
                            />
                        </div>
                    )}
                    <div className="w-36">
                        <Select
                            label="Source"
                            value={canalFiltre}
                            onChange={setCanalFiltre}
                            options={[
                                { value: '', label: 'Toutes' },
                                ...canauxDispo.map(c => ({ value: c, label: c })),
                            ]}
                        />
                    </div>
                    <Button variant='outline' startIcon={<Search size={14} />} onClick={() => LoadData(tab)}>Rechercher</Button>
                </div>

                {/* Période — onglet Historique uniquement */}
                {tab === 'HISTORIQUE' && (
                    <div className="mt-3 pt-3 border-t border-dashed border-[var(--eb-border)]">
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                            <CalendarDays size={13} className="text-[var(--eb-text-muted)]" />
                            <span className="text-xs font-semibold uppercase tracking-wide text-[var(--eb-text-muted)]">Période</span>
                            <div className="flex items-center gap-1 flex-wrap">
                                {([
                                    { key: 'today', label: "Aujourd'hui" },
                                    { key: '7j', label: '7 derniers jours' },
                                    { key: '30j', label: '30 derniers jours' },
                                    { key: 'month', label: 'Ce mois' },
                                ] as const).map(p => (
                                    <button
                                        key={p.key}
                                        type="button"
                                        onClick={() => applyPreset(p.key)}
                                        className="px-2.5 py-1 text-xs font-medium rounded-lg border border-[var(--eb-border)] text-[var(--eb-text-muted)] hover:border-[var(--eb-primary)] hover:text-[var(--eb-primary)] transition-colors"
                                    >
                                        {p.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="flex items-end gap-2 flex-wrap">
                            <div className="w-44">
                                <Input type="date" label="Du" value={dateDebut} onChange={(e: any) => setDateDebut(e.target.value)} />
                            </div>
                            <div className="w-44">
                                <Input type="date" label="Au" value={dateFin} onChange={(e: any) => setDateFin(e.target.value)} />
                            </div>
                            <Button variant='secondary' size='sm' onClick={() => LoadData('HISTORIQUE')}>Appliquer la période</Button>
                        </div>
                    </div>
                )}
            </div>

            {isEmpty && !loading ? (
                <Empty description={tab === 'EN_COURS' ? 'Aucune demande en cours' : "Aucune demande dans l'historique"} />
            ) : (
                <div className="space-y-3">
                    {demandesAff.map((d: any, i: number) => (
                        <div key={d.CodeDemande ?? i} className="eb-card p-4">
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                                <div className="flex items-center gap-3 flex-wrap">
                                    <span className="font-mono text-sm font-bold text-[var(--eb-text)]">{d.CodeDemande || '—'}</span>
                                    {d.Canal && (
                                        <Badge color="blue"><span className="inline-flex items-center gap-1"><Globe size={11} />{d.Canal}</span></Badge>
                                    )}
                                    <StatutDemandeBadge statut={d.Etat ?? d.StatutDemande} />
                                    {d.NbFormulairesAjustes > 0 && <Badge color="amber">{d.NbFormulairesAjustes} form. ajusté{d.NbFormulairesAjustes > 1 ? 's' : ''}</Badge>}
                                </div>
                                <div className="flex items-center gap-3 flex-wrap text-xs text-[var(--eb-text-muted)]">
                                    {(d.NomClient || d.PrenomClient || d.Telephone) && (
                                        <span className="inline-flex items-center gap-1 text-[var(--eb-text)]">
                                            <UserIcon size={12} />
                                            {[d.PrenomClient, d.NomClient].filter(Boolean).join(' ') || '—'}
                                            {d.Telephone ? ` · ${d.Telephone}` : ''}
                                        </span>
                                    )}
                                    {(d.LibelleFiliale || d.CodeFiliale) && (
                                        <span className="inline-flex items-center gap-1"><MapPin size={12} />{d.LibelleFiliale || d.CodeFiliale}</span>
                                    )}
                                    {d.MontantDeclare != null && (
                                        <span className="inline-flex items-center gap-1"><Coins size={12} />{formatMontant(d.MontantDeclare)}</span>
                                    )}
                                    <span>{formatDate(d.DateCreation)}</span>
                                </div>
                            </div>
                            {renderTickets(ticketsParDemande[d.CodeDemande] || [], d)}
                            {renderFormulaires(formulairesParDemande[d.CodeDemande] || [], d)}
                            {(ticketsParDemande[d.CodeDemande] || []).length > 0 && (
                                <div className="mt-3 border-t border-[var(--eb-border)] pt-3 flex items-center gap-2 flex-wrap">
                                    <Button
                                        variant='outline'
                                        size="sm"
                                        startIcon={<PencilLine size={14} />}
                                        onClick={() => openAjust(d)}
                                    >Ajuster opérations / billetage</Button>
                                    {d.AvecBilletage && (
                                        <Button
                                            variant='outline'
                                            size="sm"
                                            startIcon={<Printer size={14} />}
                                            loading={printLoading === `bl-${d.CodeDemande}`}
                                            onClick={() => imprimerBordereau(d)}
                                        >Bordereau de billetage</Button>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}

                    {/* Tickets orphelins (sans demande dans la liste) */}
                    {ticketsOrphelins.length > 0 && (
                        <div className="eb-card p-4">
                            <div className="text-sm font-bold text-[var(--eb-text)]">Tickets sans demande associée</div>
                            {renderTickets(ticketsOrphelins, null)}
                        </div>
                    )}
                </div>
            )}

            <TableStatusBar
                count={demandesAff.length}
                loading={loading}
                onRefresh={() => LoadData(tab)}
            />

            {/* Confirmation d'annulation */}
            <Modal
                open={confirm.open}
                onClose={() => setConfirm({ ...confirm, open: false })}
                title="Annuler le ticket"
                footer={
                    <>
                        <Button variant='outline' onClick={() => setConfirm({ ...confirm, open: false })}>Fermer</Button>
                        <Button
                            variant='danger'
                            loading={actionLoading}
                            onClick={AnnulerTicket}
                        >Confirmer l'annulation</Button>
                    </>
                }
            >
                <p className="text-sm text-[var(--eb-text)]">
                    Voulez-vous annuler le ticket <strong className="font-mono">{confirm.ticket?.NumeroTicket || confirm.ticket?.CodeT}</strong>
                    {confirm.ticket?.LibelleService ? <> ({confirm.ticket.LibelleService})</> : null} ?
                </p>
                <div className="mt-3">
                    <Input
                        label="Motif d'annulation"
                        placeholder="Saisissez le motif (optionnel)"
                        value={reason}
                        onChange={(e: any) => setReason(e.target.value)}
                    />
                </div>
            </Modal>

            {/* ----- Drawer : ajuster un formulaire rempli ----- */}
            <FormDrawer
                open={editForm.open}
                onClose={() => setEditForm(prev => ({ ...prev, open: false }))}
                title={`Ajuster — ${editForm.form?.LibelleForm || editForm.form?.CodeForm || 'Formulaire'}`}
                hint="Corrigez les valeurs saisies par le client. Le formulaire complet est renvoyé ; chaque ajustement est tracé avec le motif."
                footer={
                    <>
                        <Button variant='outline' onClick={() => setEditForm(prev => ({ ...prev, open: false }))}>Fermer</Button>
                        <Button variant='primary' loading={editForm.saving} onClick={submitEditForm}>Enregistrer l'ajustement</Button>
                    </>
                }
            >
                <Spin spinning={editForm.loading}>
                    <div className="space-y-3">
                        {editForm.champs.map(c => {
                            const v = editForm.valeurs[c.CodeChamp]
                            const err = editForm.erreurs[c.CodeChamp]
                            const set = (val: any) => setEditForm(prev => ({ ...prev, valeurs: { ...prev.valeurs, [c.CodeChamp]: val } }))
                            const label = <>{c.Libelle || c.CodeChamp}{c.Obligatoire ? ' *' : ''}</>
                            const common = { label, error: !!err, helperText: err }
                            if (c.TypeChamp === 'SELECT') {
                                return (
                                    <div key={c.CodeChamp}>
                                        <Select
                                            label={label}
                                            value={v ?? ''}
                                            onChange={set}
                                            options={parseOptions(c.OptionsJson).map(o => ({ value: o, label: o }))}
                                            placeholder={c.Placeholder || 'Sélectionner'}
                                        />
                                        {err && <p className="mt-1 text-xs text-red-500">{err}</p>}
                                    </div>
                                )
                            }
                            if (c.TypeChamp === 'CHECKBOX') {
                                return (
                                    <label key={c.CodeChamp} className="flex items-center gap-2 text-sm text-[var(--eb-text)]">
                                        <input type="checkbox" className="h-4 w-4 accent-[var(--eb-primary)]"
                                            checked={!!v} onChange={(e) => set(e.target.checked)} />
                                        {label}
                                        {err && <span className="text-xs text-red-500">{err}</span>}
                                    </label>
                                )
                            }
                            if (c.TypeChamp === 'TEXTAREA') {
                                return (
                                    <div key={c.CodeChamp}>
                                        <label className="block text-xs font-semibold text-[var(--eb-text-muted)] mb-1.5">{label}</label>
                                        <textarea
                                            className={`w-full px-3 py-2 text-sm rounded-lg border bg-[var(--eb-surface)] text-[var(--eb-text)] focus:outline-none focus:ring-2 focus:ring-[var(--eb-primary)]/40 ${err ? 'border-red-500' : 'border-[var(--eb-border)]'}`}
                                            rows={3} placeholder={c.Placeholder} value={v ?? ''} onChange={(e) => set(e.target.value)} />
                                        {err && <p className="mt-1 text-xs text-red-500">{err}</p>}
                                    </div>
                                )
                            }
                            return (
                                <Input key={c.CodeChamp} {...common}
                                    type={c.TypeChamp === 'NUMBER' ? 'number' : c.TypeChamp === 'DATE' ? 'date' : 'text'}
                                    placeholder={c.Placeholder}
                                    value={v ?? ''} onChange={(e: any) => set(e.target.value)} />
                            )
                        })}
                        {!editForm.loading && editForm.champs.length === 0 && (
                            <p className="text-sm text-[var(--eb-text-muted)]">Aucune valeur récupérée pour ce formulaire.</p>
                        )}
                        <Input
                            label="Motif d'ajustement *"
                            placeholder="Ex. Date de naissance corrigée"
                            value={editForm.motif}
                            onChange={(e: any) => setEditForm(prev => ({ ...prev, motif: e.target.value }))}
                        />
                    </div>
                </Spin>
            </FormDrawer>

            {/* ----- Drawer : lignes d'opération de la demande (ajustement) ----- */}
            <FormDrawer
                open={ajust.open}
                onClose={() => setAjust(prev => ({ ...prev, open: false }))}
                title={`Ajuster — ${ajust.demande?.CodeDemande || ''}`}
                hint="Cliquez sur une ligne d'opération pour la déplier et corriger le billetage, le compte, l'origine, le montant ou le motif."
                footer={<Button variant='outline' onClick={() => { setAjust(prev => ({ ...prev, open: false })); setEditLigne(null) }}>Fermer</Button>}
            >
                <Spin spinning={ajust.loading}>
                    {ajust.lignes.length === 0 && !ajust.loading ? (
                        <Empty description="Aucune ligne d'opération trouvée" />
                    ) : (
                        <div className="space-y-2">
                            {ajust.lignes.map((l: any, i: number) => {
                                const key = l?.CodeOperationLigne ?? i
                                const expanded = editLigne?.key === key
                                const avecBil = !!(l?.NecessiteBilletage || l?.AvecBilletage || l?.Billetage || l?.LignesBilletage)
                                return (
                                    <div key={key} className={`rounded-lg border transition-colors ${expanded ? 'border-[var(--eb-primary)]' : 'border-[var(--eb-border)]'}`}>
                                        {/* En-tête cliquable */}
                                        <button
                                            type="button"
                                            onClick={() => toggleLigne(l, i)}
                                            className="w-full flex items-center justify-between gap-3 p-3 text-left"
                                        >
                                            <div className="min-w-0">
                                                <div className="text-sm font-semibold text-[var(--eb-text)] truncate">
                                                    {l?.LibelleOperation || l?.Libelle || `Ligne ${key}`}
                                                </div>
                                                <div className="text-xs text-[var(--eb-text-muted)] flex items-center gap-2 flex-wrap">
                                                    {l?.NumeroCompteSaisi && <span className="font-mono">{l.NumeroCompteSaisi}</span>}
                                                    {(l?.MontantSaisi ?? l?.MontantDeclare) != null && <span>{formatMontant(l.MontantSaisi ?? l.MontantDeclare)}</span>}
                                                    {(l?.LibelleOrigine || l?.CodeOrigine) && <span>{l.LibelleOrigine || l.CodeOrigine}</span>}
                                                    {avecBil && <Badge color="violet"><span className="inline-flex items-center gap-1"><Coins size={11} />Billetage</span></Badge>}
                                                </div>
                                            </div>
                                            <ChevronDown size={16} className={`shrink-0 text-[var(--eb-text-muted)] transition-transform ${expanded ? 'rotate-180' : ''}`} />
                                        </button>

                                        {/* Formulaire d'ajustement déplié */}
                                        {expanded && editLigne && (
                                            <div className="border-t border-[var(--eb-border)] p-3 space-y-3">
                                                <Input
                                                    label="N° de compte"
                                                    value={editLigne.compte}
                                                    onChange={(e: any) => setEditLigne({ ...editLigne, compte: e.target.value })}
                                                />
                                                <Input
                                                    label="Montant"
                                                    type="number"
                                                    value={editLigne.montant}
                                                    onChange={(e: any) => setEditLigne({ ...editLigne, montant: e.target.value })}
                                                />
                                                <Select
                                                    label="Origine des fonds"
                                                    value={editLigne.origine}
                                                    onChange={(v: any) => setEditLigne({ ...editLigne, origine: v })}
                                                    options={originesRef.map((o: any) => ({ value: o.CodeOrigine, label: o.Libelle || o.CodeOrigine }))}
                                                    placeholder="Sélectionner une origine"
                                                />
                                                <Input
                                                    label="Motif de l'opération"
                                                    value={editLigne.motifOp}
                                                    onChange={(e: any) => setEditLigne({ ...editLigne, motifOp: e.target.value })}
                                                />

                                                {/* Billetage (recomptage) */}
                                                {avecBil && (
                                                    <div className="rounded-lg border border-dashed border-[var(--eb-border)] p-3 space-y-2">
                                                        <div className="text-xs font-semibold uppercase text-[var(--eb-text-muted)]">Billetage</div>
                                                        {(['BILLET', 'PIECE'] as const).map(type => {
                                                            const denoms = denomsRef
                                                                .filter((d: any) => String(d.TypeCoupure).toUpperCase() === type)
                                                                .filter((d: any) => !l?.CodeDevise || d.CodeDevise === l.CodeDevise)
                                                                .sort((a: any, b: any) => Number(b.Valeur) - Number(a.Valeur))
                                                            if (!denoms.length) return null
                                                            return (
                                                                <div key={type}>
                                                                    <div className="text-[11px] font-semibold text-[var(--eb-text-muted)] mb-1">
                                                                        {type === 'BILLET' ? 'Billets' : 'Pièces'}
                                                                    </div>
                                                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                                                        {denoms.map((d: any) => {
                                                                            const dk = `${d.TypeCoupure}-${d.Valeur}`
                                                                            const q = editLigne.qtes[dk] || 0
                                                                            const setQ = (n: number) => setEditLigne({ ...editLigne, qtes: { ...editLigne.qtes, [dk]: Math.max(0, n) } })
                                                                            return (
                                                                                <div key={dk} className={`rounded-lg border p-2 ${q > 0 ? 'border-[var(--eb-primary)] bg-[var(--eb-primary-soft)]' : 'border-[var(--eb-border)]'}`}>
                                                                                    <div className="text-xs text-[var(--eb-text-muted)]">{d.CodeDevise}</div>
                                                                                    <div className="text-sm font-bold text-[var(--eb-text)] tabular-nums">{formatMontant(d.Valeur)}</div>
                                                                                    <div className="mt-1.5 flex items-center justify-between">
                                                                                        <button type="button" disabled={q === 0} onClick={() => setQ(q - 1)}
                                                                                            className="h-7 w-7 rounded-md border border-[var(--eb-border)] inline-flex items-center justify-center disabled:opacity-30">
                                                                                            <Minus size={13} />
                                                                                        </button>
                                                                                        <span className="text-sm font-bold tabular-nums">{q}</span>
                                                                                        <button type="button" onClick={() => setQ(q + 1)}
                                                                                            className="h-7 w-7 rounded-md border border-[var(--eb-border)] inline-flex items-center justify-center">
                                                                                            <Plus size={13} />
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                            )
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            )
                                                        })}
                                                        <div className="flex items-center justify-between text-sm font-semibold">
                                                            <span>Total billetage</span>
                                                            <span className="tabular-nums">{formatMontant(
                                                                denomsRef.reduce((s: number, d: any) => s + Number(d.Valeur) * (editLigne.qtes[`${d.TypeCoupure}-${d.Valeur}`] || 0), 0)
                                                            )}</span>
                                                        </div>
                                                    </div>
                                                )}

                                                <Input
                                                    label="Motif d'ajustement *"
                                                    placeholder="Ex. Compte corrigé / Recomptage au guichet"
                                                    value={editLigne.motif}
                                                    onChange={(e: any) => setEditLigne({ ...editLigne, motif: e.target.value })}
                                                />
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <Button
                                                        variant='primary'
                                                        size="sm"
                                                        loading={editLigne.savingOp}
                                                        onClick={() => submitOpEdit(l)}
                                                    >Enregistrer l'opération</Button>
                                                    {avecBil && (
                                                        <Button
                                                            variant='secondary'
                                                            size="sm"
                                                            startIcon={<Coins size={14} />}
                                                            loading={editLigne.savingBil}
                                                            onClick={() => submitBilEdit(l)}
                                                        >Enregistrer le billetage</Button>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </Spin>
            </FormDrawer>
        </Spin>
    );
}
